import { ElementType, forwardRef, ReactNode } from "react";
import { Box, BoxProps } from "@mui/material";
import { OverflowTooltip } from "../OverflowTooltip";
import { COLOR_CONSTANTS, TYPOGRAPHY_SCALE } from "../../constants/styles";

/**
 * 사이드 패널의 메인 헤더 — 제목/부제 + 우측 액션 슬롯. 최대 너비 320px, 제목 오버플로우 시 툴팁 표시.
 */
export interface SideMainHeaderProps extends BoxProps {
  subTitle?: string;
  /**
   * 부제를 제목 아래에 쌓을지, 제목 오른쪽 끝에 둘지.
   * 레이어 패널처럼 부제가 제목의 설명이 아니라 목록의 요약값(전체 개수)일 때
   * `"right"` 를 쓴다 — 제목 아래를 비워 목록이 바로 시작한다.
   * 문구 크기는 배치에 따라 다르다: `"below"` 는 본문(body2), `"right"` 는 요약값
   * 성격이라 한 단계 작은 caption1 로 렌더된다(색은 두 배치가 같다).
   */
  subTitlePlacement?: "below" | "right";
  title?: string;
  /** 제목 태그 — 생략하면 h5. 페이지 헤딩 단계에 맞춰야 할 때만 넘긴다 */
  titleComponent?: ElementType;
  /** 우측 액션 슬롯 (예: 추가 버튼) */
  rightSlot?: ReactNode;
}

const container = {
  width: "100%",
  maxWidth: 320,
  padding: "16px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "8px",
  backgroundColor: "white",
} as const;

/*
 * 제목(h5 20px)과 우측 요약(caption1 12px)은 글자 크기가 달라 flex `center` 로는
 * 밑선이 맞지 않는다 — 중앙정렬된 줄박스의 베이스라인은 중심에서 `ascent − fontSize/2`
 * 만큼 내려가므로(라인높이와 무관) 두 글자의 밑선이 ~2px 어긋나 보인다.
 * 그래서 right 배치에서는 제목과 요약을 컨테이너 직계 형제로 두고 둘만 baseline
 * 그룹으로 묶는다. 행 높이를 제목 줄박스가 지배하므로 제목 위치는 center 때와 같고,
 * 요약만 제목 밑선으로 내려와 붙는다. rightSlot(버튼류)은 center 를 유지한다.
 *
 * 이 배치의 축소 규칙: 남은 공간을 채우는 건 제목 쪽(`leftSlot`, flex-basis 0)뿐이라
 * 제목이 길어지면 제목이 먼저 줄고 요약(`names`)은 제 너비를 지킨다. 요약 문구 자체가
 * 남은 폭을 넘길 때만 `names` 가 줄고, 안쪽 `nameContainer` 의 말줄임으로 잘린다.
 */
const baselineSelf = {
  alignSelf: "baseline",
} as const;

const leftSlot = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  gap: "4px",
  minWidth: 0,
  flex: 1,
} as const;

const titleContainer = {
  maxWidth: 250,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
} as const;

const titleText = {
  ...TYPOGRAPHY_SCALE.h5,
  color: COLOR_CONSTANTS.text.primary,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  width: "100%",
} as const;

const names = {
  maxWidth: 250,
  display: "flex",
  alignItems: "center",
  lineHeight: "20px",
  overflow: "hidden",
} as const;

const nameContainer = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  width: "100%",
} as const;

// 액션 슬롯만 감싼다 — 어떤 경우에도 줄지 않는다(제목·요약의 축소는 `baselineSelf` 주석).
const rightArea = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  flexShrink: 0,
} as const;

const nameText = {
  ...TYPOGRAPHY_SCALE.body2,
  color: COLOR_CONSTANTS.text.secondary,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
} as const;

const summaryText = {
  fontSize: TYPOGRAPHY_SCALE.caption1.fontSize,
  lineHeight: TYPOGRAPHY_SCALE.caption1.lineHeight,
  letterSpacing: TYPOGRAPHY_SCALE.caption1.letterSpacing,
} as const;

export const SideMainHeader = forwardRef<HTMLDivElement, SideMainHeaderProps>(
  (
    {
      title,
      titleComponent,
      subTitle,
      subTitlePlacement = "below",
      rightSlot,
      ...props
    },
    ref
  ) => {
    const isRightPlaced = subTitlePlacement === "right";

    const subTitleNode = subTitle ? (
      <Box sx={[names, isRightPlaced && baselineSelf]}>
        <Box sx={nameContainer}>
          <OverflowTooltip
            text={subTitle}
            variant="body2"
            placement="bottom"
            sx={[
              nameText,
              { paddingRight: "2px" },
              isRightPlaced && summaryText,
            ]}
          />
        </Box>
      </Box>
    ) : null;

    return (
      <Box ref={ref} {...props} sx={container}>
        <Box sx={[leftSlot, isRightPlaced && baselineSelf]}>
          {title && (
            <Box sx={titleContainer}>
              <OverflowTooltip
                text={title}
                variant="h5"
                component={titleComponent}
                placement="top"
                sx={titleText}
              />
            </Box>
          )}
          {!isRightPlaced && subTitleNode}
        </Box>
        {isRightPlaced && subTitleNode}
        {rightSlot && <Box sx={rightArea}>{rightSlot}</Box>}
      </Box>
    );
  }
);

SideMainHeader.displayName = "SideMainHeader";
